import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PricingSheet from "@/components/PricingSheet";
import ShowcaseVideo from "@/components/ShowcaseVideo";
import useReveal from "@/lib/useReveal";
import {
  Phone, MessageSquare, Star, Globe, MessageCircle, TrendingUp, PlayCircle, Layers,
  ShieldCheck, Lock, BadgeCheck, Stethoscope, Sparkles, Flower2, Wrench, Home,
  Scale, Scissors, Dumbbell, ArrowRight, Check, Plus, Calculator,
} from "lucide-react";

export default function Landing() {
  useReveal();
  return (
    <div className="grain min-h-screen">
      <Nav />
      <main>
        <Hero />
        <ComplianceBand />
        <StatStrip />
        <Services />
        <ShowcaseVideo />
        <Industries />
        <HowItWorks />
      <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative pt-12 lg:pt-20 pb-16 lg:pb-24 overflow-hidden" id="hero">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="reveal flex items-center gap-3 mb-8 flex-wrap">
          <span className="tag">
            <span className="w-1.5 h-1.5 rounded-full bg-rust pulse-dot"></span>
            AI growth team for service businesses
          </span>
          <span className="badge-compliance"><span className="dot"></span>HIPAA-compliant · SOC 2 aligned</span>
        </div>

        <h1 className="reveal display text-[12vw] sm:text-[10vw] lg:text-[120px] xl:text-[140px] font-medium max-w-[14ch]" data-testid="hero-headline">
          Missed calls.<br />Dead DMs.<br />
          <span className="display-italic text-rust">Forgotten leads.</span>
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 mt-10 lg:mt-14 items-end">
          <div className="reveal lg:col-span-6">
            <p className="text-lg lg:text-xl leading-snug text-ink/80 max-w-[44ch]">
              We build AI receptionists, AI content, and auto-booking for service businesses — so you stop losing 20–40% of inbound revenue while you're at work.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link to="/audit" className="btn-primary px-6 py-3.5 rounded-full text-sm font-medium tracking-wide" data-testid="hero-cta-audit">
                Get my free AI audit
              </Link>
              <Link to="/roi" className="btn-ghost px-6 py-3.5 rounded-full text-sm font-medium tracking-wide inline-flex items-center gap-2" data-testid="hero-cta-roi">
                <Calculator size={14} /> ROI calculator
              </Link>
            </div>
            <div className="flex items-center gap-4 mt-8">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-rust/80 border-2 border-cream flex items-center justify-center text-cream text-xs font-semibold">SF</div>
                <div className="w-8 h-8 rounded-full bg-ink border-2 border-cream flex items-center justify-center text-cream text-xs font-semibold">RD</div>
                <div className="w-8 h-8 rounded-full bg-mute border-2 border-cream flex items-center justify-center text-cream text-xs font-semibold">AL</div>
                <div className="w-8 h-8 rounded-full bg-paper border-2 border-cream flex items-center justify-center text-ink text-xs font-semibold">+</div>
              </div>
              <div className="text-sm text-ink/70">
                <div className="flex items-center gap-1 text-rust">★★★★★ <span className="text-ink/60 ml-1">4.9</span></div>
                <span className="text-xs">Trusted by service businesses across the US</span>
              </div>
            </div>
          </div>

          <div className="reveal lg:col-span-6 relative">
            <div className="device-glow absolute inset-0"></div>
            <div className="relative grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-7 card-paper rounded-2xl p-5 lift animate-float">
                <div className="flex items-center justify-between text-xs mono uppercase text-ink/60">
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-rust pulse-dot"></span> Inbound · 11:43 PM</span>
                  <span>AI handled</span>
                </div>
                <div className="mt-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-ink text-cream flex items-center justify-center font-bold">DM</div>
                    <div>
                      <div className="font-semibold">Diana M.</div>
                      <div className="text-sm text-ink/60">New patient inquiry</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm leading-relaxed">
                    <span className="text-ink/50 mono text-[10px] uppercase">Message</span>
                    <p className="mt-1">"Hi, do you have any availability this week for a consult? Saw your reviews."</p>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rust/10 text-rust mono uppercase">Booked</span>
                    <span className="text-xs text-ink/60">Thu 2:30pm · intake form sent</span>
                  </div>
                </div>
              </div>
              <div className="col-span-12 sm:col-span-5 flex flex-col gap-3">
                <div className="card-ink rounded-2xl p-5 lift">
                  <div className="text-xs mono uppercase tracking-wider text-cream/60">This month</div>
                  <div className="display text-5xl mt-2">+ $9.4k</div>
                  <div className="text-sm text-cream/70 mt-1">recovered revenue</div>
                </div>
                <div className="card-paper rounded-2xl p-5 lift">
                  <div className="text-xs mono uppercase tracking-wider text-mute">Coverage</div>
                  <div className="display text-5xl mt-2">24<span className="text-rust">/7</span></div>
                  <div className="text-sm text-ink/60 mt-1">calls · DMs · forms</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- COMPLIANCE ---------- */
function ComplianceBand() {
  const items = [
    { Icon: ShieldCheck, title: "HIPAA-compliant", sub: "For medical & dental clinics" },
    { Icon: Lock, title: "End-to-end encrypted", sub: "PHI & PII protected" },
    { Icon: BadgeCheck, title: "SOC 2 aligned", sub: "Enterprise-grade controls" },
    { Icon: ShieldCheck, title: "BAA available", sub: "For covered entities" },
  ];
  return (
    <section className="py-8 border-y border-ink/10 bg-paper/40">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {items.map(({ Icon, title, sub }) => (
            <div key={title} className="flex items-center gap-3 text-sm">
              <Icon size={20} className="text-rust shrink-0" strokeWidth={1.6} />
              <div>
                <div className="font-semibold">{title}</div>
                <div className="text-xs text-mute">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- MARQUEE ---------- */
function TrustMarquee() {
  const items = [
    { t: "RIVERBEND DENTAL", italic: false, tracking: false },
    { t: "Glow Med Spa", italic: true, tracking: false },
    { t: "ATLAS LAW", italic: false, tracking: true },
    { t: "Sarah's Flowers", italic: false, tracking: false },
    { t: "Northwind HVAC", italic: true, tracking: false },
    { t: "BLOOM SALON", italic: false, tracking: true },
    { t: "Apex Roofing", italic: false, tracking: false },
    { t: "Pulse Physical Therapy", italic: true, tracking: false },
  ];
  const all = [...items, ...items];
  return (
    <section className="py-10 border-b border-ink/10 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="text-xs mono uppercase tracking-[0.2em] text-mute mb-6 text-center">
          Built for shops, clinics, studios, and service teams
        </div>
        <div className="relative">
          <div className="flex gap-14 whitespace-nowrap animate-marquee">
            {all.map((it, i) => (
              <span
                key={i}
                className={`text-2xl text-ink/70 ${it.italic ? "display-italic" : "display"} ${it.tracking ? "tracking-widest" : ""}`}
              >
                {it.t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SLIM STAT STRIP ---------- */
function StatStrip() {
  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="reveal max-w-[34ch] mb-12">
          <span className="tag mb-4">The leak</span>
          <h2 className="display text-4xl lg:text-6xl mt-4">
            Every shop has a <span className="display-italic text-rust">revenue leak</span> they can't see.
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-8 lg:gap-14">
          <Stat n="20–40%" body="of inbound calls go unanswered at the average service business." />
          <Stat n="$5–8k" body="average monthly revenue our clients recover after going live." />
          <Stat n="30s" body="time before a customer hangs up and dials your competitor." />
        </div>
        <div className="mt-12">
          <Link to="/roi" className="btn-rust px-6 py-3 rounded-full text-sm font-medium inline-flex items-center gap-2" data-testid="leak-cta-roi">
            <Calculator size={14} /> Run the math on my shop
          </Link>
        </div>
      </div>
    </section>
  );
}
function Stat({ n, body }) {
  return (
    <div className="reveal">
      <div className="stat-num text-6xl lg:text-7xl text-ink">{n}</div>
      <div className="h-px bg-ink/15 my-5"></div>
      <p className="text-ink/70 max-w-[28ch] text-[15px]">{body}</p>
    </div>
  );
}

/* ---------- SERVICES (simplified) ---------- */
function Services() {
  const items = [
    { Icon: Phone, title: "AI Receptionist", body: "Answers 24/7, qualifies, books, captures payment." },
    { Icon: MessageSquare, title: "Missed-Call Text-Back", body: "Instant SMS the moment a call is missed." },
    { Icon: Star, title: "Review Engine", body: "Auto-request reviews after every job. 5× velocity." },
    { Icon: Globe, title: "GBP Optimization", body: "Profile, posts, Q&A, photos. Top 3 map pack." },
    { Icon: MessageCircle, title: "DM Setter (IG/FB)", body: "AI replies, qualifies, books straight to calendar." },
    { Icon: TrendingUp, title: "Lead Nurture", body: "Email + SMS sequences that close cold leads." },
    { Icon: PlayCircle, title: "VSL & Web Pages", body: "High-converting pages. 2–5× lead conversion." },
    { Icon: Layers, title: "Social Content", body: "5–7 posts/week, reels, captions — done for you." },
  ];
  return (
    <section id="services" className="py-20 lg:py-28 bg-paper/60 border-y border-ink/10">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="reveal max-w-[30ch] mb-12">
          <span className="tag mb-4">What we build</span>
          <h2 className="display text-4xl lg:text-6xl mt-4">
            A complete <span className="display-italic text-rust">growth team</span> — without the hires.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map(({ Icon, title, body }, i) => (
            <div key={title} className="reveal card-paper rounded-2xl p-5 lift" data-testid={`service-card-${i}`}>
              <div className="svc-icon"><Icon size={20} strokeWidth={1.6} /></div>
              <h3 className="display text-xl mt-4">{title}</h3>
              <p className="text-ink/70 mt-1.5 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- INDUSTRIES (slim) ---------- */
function Industries() {
  const items = [
    { Icon: Stethoscope, title: "Medical & Dental", sub: "HIPAA · BAA available" },
    { Icon: Sparkles, title: "Med Spas", sub: "Aesthetics · wellness" },
    { Icon: Flower2, title: "Retail & Florists", sub: "Local commerce" },
    { Icon: Wrench, title: "Auto & Mechanics", sub: "Mobile · shops" },
    { Icon: Home, title: "Home Services", sub: "HVAC · plumbing · roofing" },
    { Icon: Scale, title: "Legal & Accounting", sub: "Solos · small firms" },
    { Icon: Scissors, title: "Salons & Spas", sub: "Hair · nails · barbers" },
    { Icon: Dumbbell, title: "Fitness & Wellness", sub: "Studios · trainers · PT" },
  ];
  return (
    <section id="industries" className="py-20 lg:py-28">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="reveal max-w-[30ch] mb-10">
          <span className="tag mb-4">Built for any service business</span>
          <h2 className="display text-4xl lg:text-6xl mt-4">
            Same plumbing. <span className="display-italic text-rust">Different verticals.</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(({ Icon, title, sub }) => (
            <div key={title} className="industry-pill rounded-2xl p-5 cursor-pointer group">
              <div className="industry-icon text-rust"><Icon size={22} strokeWidth={1.6} /></div>
              <div className="display text-lg mt-4">{title}</div>
              <div className="text-xs text-mute mt-1 mono uppercase tracking-wider group-hover:text-cream/60">{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- HOW IT WORKS (slim) ---------- */
function HowItWorks() {
  const steps = [
    ["01 · 5 min", "Free AI audit.", "Submit your website, our AI generates a custom audit in seconds — your missed-call estimate, GBP gaps, conversion leaks."],
    ["02 · 15 min", "Strategy call.", "If the audit makes sense, we hop on a 15-minute call. We map the exact build for your business and tell you what it costs."],
    ["03 · 7 days", "Build & go live.", "We build the AI receptionist on your voice, wire up automations, and flip it on. By Day 7 your AI is booking jobs."],
    ["04 · 30 days", "See the recovered revenue.", "Your dashboard shows recovered calls, booked jobs, and dollar impact. Most clients break even by week one."],
  ];
  return (
    <section id="how" className="py-20 lg:py-28 bg-paper/60 border-y border-ink/10">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="reveal flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="tag mb-4">How it works</span>
            <h2 className="display text-4xl lg:text-6xl mt-4 max-w-[18ch]">
              Audit in 5 min. Live in <span className="display-italic text-rust">7 days</span>.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map(([n, title, body]) => (
            <div key={n} className="reveal card-paper rounded-2xl p-6 lift">
              <div className="mono text-xs uppercase tracking-wider text-rust">Step {n}</div>
              <h3 className="display text-xl mt-4">{title}</h3>
              <p className="text-ink/70 mt-2 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const items = [
    ["Is AI Automate Systems HIPAA-compliant?", "Yes. For any covered entity (medical practices, dental clinics, therapy, chiropractic, etc.) we sign a Business Associate Agreement before go-live. PHI is encrypted in transit and at rest, infrastructure meets HIPAA technical safeguards, and access is role-based with full audit logs."],
    ["Will it actually sound like a person?", "Yes. We use the latest natural-voice AI plus a script trained specifically on your business's tone, services, and pricing. Most callers don't realize until they ask — and we tell the truth when they do."],
    ["What if it can't answer a question?", "It says \"let me have someone call you back,\" captures the details, and texts your team instantly. The AI learns from every escalation and handles more on the next round."],
    ["Do I have to change my phone number or CRM?", "No. We forward only the calls you'd otherwise miss — your main number stays the same. We integrate with most major CRMs, calendars, and EHR/PMS systems."],
    ["How fast does it pay for itself?", "Most clients cover their fee in the first 1–2 booked jobs of the month. We guarantee positive ROI in 30 days or you get the next month free."],
    ["Is there a contract?", "Month-to-month. Cancel any time. Annual plans get 2 months free. We earn the next month, every month."],
  ];
  return (
    <section id="faq" className="py-20 lg:py-28">
      <div className="max-w-[920px] mx-auto px-6 lg:px-10">
        <div className="reveal text-center mb-10">
          <span className="tag">FAQ</span>
          <h2 className="display text-4xl lg:text-6xl mt-5">Honest answers.</h2>
        </div>
        <div className="space-y-3">
          {items.map(([q, a], i) => (
            <details key={q} className="reveal group card-paper rounded-xl p-6 cursor-pointer" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between list-none">
                <span className="display text-lg">{q}</span>
                <Plus size={20} className="text-rust group-open:rotate-45 transition-transform" />
              </summary>
              <p className="text-ink/70 mt-4 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section id="cta" className="py-24 lg:py-36 bg-ink text-cream relative overflow-hidden">
      <div className="absolute -right-20 -top-20 w-[600px] h-[600px] rounded-full bg-rust/20 blur-3xl"></div>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative">
        <div className="reveal max-w-[24ch]">
          <span className="tag tag-dark">Ready when you are</span>
          <h2 className="display text-5xl lg:text-[110px] mt-6 leading-[0.92]">
            Stop losing jobs to <span className="display-italic text-ember">voicemail</span>.
          </h2>
          <p className="text-cream/70 text-lg mt-6 max-w-[44ch]">
            Three quick paths. Pick yours.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/audit" className="btn-cream px-6 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2" data-testid="finalcta-audit">
              Run my AI audit <ArrowRight size={14} />
            </Link>
            <PricingSheet trigger={
              <button className="border border-cream/30 px-6 py-3.5 rounded-full text-sm font-medium hover:bg-cream/10 transition" data-testid="finalcta-pricing">
                See pricing →
              </button>
            } />
          </div>
          <div className="mt-12 pt-8 border-t border-cream/15 grid sm:grid-cols-3 gap-6">
            <div><div className="display text-3xl">5 min</div><div className="text-cream/60 text-sm mt-1">Audit length</div></div>
            <div><div className="display text-3xl">7 days</div><div className="text-cream/60 text-sm mt-1">Average go-live</div></div>
            <div><div className="display text-3xl">30-day</div><div className="text-cream/60 text-sm mt-1">ROI guarantee</div></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Anchor div for #showcase scroll target (ShowcaseVideo doesn't have id) */
